import type { AdminMenuFeatureKey } from "./permission.features";

/**
 * Izin panel admin dari sisi klien, dibaca sekali lalu dibagi ke seluruh route
 * & sidebar. Ini LAPIS KENYAMANAN: menyembunyikan menu yang tidak diizinkan.
 * Penegakan sebenarnya ada di guard endpoint (`src/middleware/admin-feature.ts`)
 * — admin yang mengetik URL langsung tetap ditolak server.
 */

export interface AdminAccess {
	allowed: readonly string[];
	isSuperAdmin: boolean;
}

/** Fail-closed: gagal baca izin ⇒ tidak ada menu bergating yang ditampilkan. */
export const NO_ADMIN_ACCESS: AdminAccess = {
	allowed: [],
	isSuperAdmin: false,
};

/** Umur cache izin; cukup lama untuk satu navigasi, cukup pendek untuk ikut perubahan. */
const CACHE_TTL_MS = 10_000;

const ENDPOINT = "/api/my-permissions";

let cached: { value: AdminAccess; at: number } | null = null;
let inflight: Promise<AdminAccess> | null = null;

async function request(): Promise<AdminAccess> {
	const res = await fetch(ENDPOINT, { credentials: "include" });
	if (!res.ok) throw new Error(`${ENDPOINT} membalas ${res.status}`);
	const json = (await res.json()) as Partial<AdminAccess>;
	return {
		allowed: Array.isArray(json.allowed) ? json.allowed : [],
		isSuperAdmin: json.isSuperAdmin === true,
	};
}

/**
 * Ambil izin panel admin (di-cache & dedup). Kegagalan jaringan TIDAK di-cache
 * supaya navigasi berikutnya mencoba lagi, dan mengembalikan "tanpa akses"
 * daripada menebak boleh.
 */
export async function loadAdminAccess(force = false): Promise<AdminAccess> {
	if (!force && cached && Date.now() - cached.at < CACHE_TTL_MS) {
		return cached.value;
	}
	if (!force && inflight) return inflight;

	inflight = request()
		.then((value) => {
			cached = { value, at: Date.now() };
			return value;
		})
		.catch((err: unknown) => {
			// Jangan ditelan tanpa jejak: menu yang hilang karena jaringan gagal
			// mudah disalahartikan sebagai izin yang dicabut.
			console.warn(
				`[ADMIN_ACCESS] Gagal memuat izin panel admin, menu bergating disembunyikan: ${
					err instanceof Error ? err.message : String(err)
				}`,
			);
			return NO_ADMIN_ACCESS;
		})
		.finally(() => {
			inflight = null;
		});
	return inflight;
}

/** Lupakan cache — dipanggil setelah matriks izin disimpan. */
export function clearAdminAccessCache(): void {
	cached = null;
}

/** True bila fitur menu diizinkan; superadmin selalu boleh. */
export function hasAdminFeature(
	access: AdminAccess,
	feature: AdminMenuFeatureKey,
): boolean {
	return access.isSuperAdmin || access.allowed.includes(feature);
}
